<template>
    <hox-notification-item
        :show-button="isCompleted"
        :show-clear-button="showClearButton"
        :show-error-message="hasFailed"
        :show-abort-button="showAbortButton"
        :status="notification.status"
        :notification="notification"
        :is-job-finished="isJobFinished"
        @clear="removeNotification"
        @notificationDeleted="deleteCompletedNotification"
    >
        <template #title>
            <span v-if="notification.job.campaignDetails && notification.job.clientDetails">
                Client:
                <strong>{{ notification.job.clientDetails?.name }}</strong>
                <br />
                Campaign:
                <strong>{{ notification.job.campaignDetails?.name }}</strong>
            </span>
            {{ title }}
        </template>
        <template #subtitle>
            {{ statusText }}
        </template>
        <template #content>
            <template v-if="isCompleted">
                <p>Here is the link to download. Please to press the button to download your deliverables.</p>
            </template>
        </template>
        <template #errorMessage>
            <p>There was an unexpected issue while preparing ads for download.</p>
        </template>
        <template #button>
            <Button size="small" target="_blank" @click="downloadHandler">
                <Icon type="md-download" data-testid="notification-item-download-icon" />
            </Button>
        </template>
    </hox-notification-item>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import nl2br from "hoxton-shared/utils/nl2br";

import { NotificationStatus, NotificationTypes } from "@/enums/notifications";
import { NotificationsAction, type Notification } from "@/store/modules/notifications";
import { resourceManagerRequest } from "@/utils/CreativeIntelligenceApi";

export default defineComponent({
    props: {
        notification: {
            required: true,
            type: Object as PropType<Notification>
        },

        showAbortButton: {
            type: Boolean
        },

        isJobFinished: {
            type: Boolean,
            default: false
        }
    },

    emits: ["notificationDeleted"],

    computed: {
        hasFailed() {
            return this.notification.status === NotificationStatus.Failed;
        },

        isCompleted() {
            return this.notification.status === NotificationStatus.Completed;
        },

        notificationMessage() {
            return nl2br(this.notification.message);
        },

        showClearButton() {
            return !this.isJobFinished && (this.hasFailed || this.isCompleted);
        },

        statusText() {
            if (this.hasFailed) {
                return "Failed";
            }
            return this.notificationMessage;
        },

        isExcelJob() {
            return this.notification.type === NotificationTypes.FlashtalkingDownloadExcel;
        },

        isAssetsJob() {
            return this.notification.type === NotificationTypes.FlashtalkingDownloadAssets;
        },

        isPsdJob() {
            return this.notification.type === NotificationTypes.PsdDownloader;
        },

        isAEJob() {
            return this.notification.type === NotificationTypes.PlainlyBatchRender;
        },

        isImagePsdJob() {
            if (!this.isPsdJob) {
                return false;
            }

            const jobContentType = this.notification.job.payload?.contentType;

            return jobContentType === "png" || jobContentType === "jpeg";
        },

        title() {
            if (this.isExcelJob) {
                return "Download your Excel";
            }
            if (this.isAssetsJob) {
                return "Download your Assets";
            }
            if (this.isImagePsdJob) {
                return "Download your image ads";
            }
            if (this.isPsdJob) {
                return "Download your PSD ads";
            }
            if (this.isAEJob) {
                return "Download your After Effects videos";
            }
            return "";
        }
    },

    methods: {
        removeNotification() {
            this.$store.dispatch(NotificationsAction.RemoveItem, this.notification.id);
        },

        async downloadHandler() {
            try {
                const body = {
                    type: this.notification.type,
                    input: this.notification.job.payload
                };

                const response = await resourceManagerRequest({
                    id: this.notification.job._id,
                    body
                });

                const fileLocations = response?.data?.output?.success;

                const signedUrl =
                    fileLocations?.excelFileLocation?.signedUrl ||
                    fileLocations?.zipFileLocation?.signedUrl ||
                    fileLocations?.assetsFileLocation?.signedUrl ||
                    fileLocations?.fileLocation?.signedUrl;

                if (signedUrl) {
                    window.open(signedUrl, "_blank");
                }
            } catch (e) {
                console.log(e);
            }
        },

        deleteCompletedNotification() {
            this.$emit("notificationDeleted");
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.download-job-notification-item__sub-progress {
    margin-top: $spacing-small;
    padding-left: $spacing-base;
    border-left: 1px solid $cmp-light-border-color;

    &-label {
        display: flex;
        justify-content: space-between;
        overflow: hidden;
        margin-bottom: $spacing-smaller;

        > :first-child {
            flex: 1;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }
}
</style>
